import { useEffect, useRef, useState } from "react";
import { Play, Pause, ExternalLink, Music2, Youtube } from "lucide-react";
import { PLATFORM_LINKS } from "@/lib/api";
import ShareEpisode from "@/components/ShareEpisode";

export default function LatestEpisodePlayer({ episode, light = false }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setProgress((a.currentTime / (a.duration || 1)) * 100);
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
    };
  }, [episode]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); }
  };

  if (!episode) return null;

  const cls = light
    ? "bg-white text-[#1E2933] border-[#DDE3E8]"
    : "bg-[#14100B] text-white border-white/10";
  const labelCls = light ? "text-[#4A6D82]" : "text-[#F4A94E]";
  const descCls = light ? "text-[#6B7A85]" : "text-white/70";
  const barBg = light ? "bg-[#E4EBEF]" : "bg-white/10";
  const barFill = light ? "bg-[#4A6D82]" : "bg-[#E63946]";
  const glow = light ? "jt-blue-glow" : "jt-red-glow";
  const playBtn = light ? "bg-[#4A6D82] hover:bg-[#2E4959]" : "bg-[#E63946] hover:bg-[#C1121F]";
  const smallBtn = light
    ? "bg-[#E4EBEF] border border-[#DDE3E8] text-[#1E2933] hover:bg-[#C5D3DC]"
    : "bg-white/10 border border-white/20 text-white hover:bg-white/20";

  return (
    <div
      data-testid="latest-episode-card"
      className={`relative rounded-3xl overflow-hidden jt-medium-shadow border ${cls}`}
    >
      <div className="grid md:grid-cols-[380px_1fr]">
        <div className="relative h-72 md:h-full min-h-[320px] bg-black">
          {episode.image_url && (
            <img src={episode.image_url} alt={episode.title} className="w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
          <button
            data-testid="latest-episode-play"
            onClick={toggle}
            className={`absolute bottom-5 left-5 h-16 w-16 rounded-full ${playBtn} flex items-center justify-center ${glow} transition-transform hover:scale-105`}
            aria-label={playing ? "Pause" : "Lecture"}
          >
            {playing ? <Pause className="w-7 h-7 text-white" /> : <Play className="w-7 h-7 text-white translate-x-[1px]" fill="currentColor" />}
          </button>
        </div>

        <div className="p-6 md:p-10 flex flex-col">
          <span className={`text-xs uppercase tracking-[0.3em] mb-3 ${labelCls}`}>Dernier épisode</span>
          <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-4">{episode.title}</h3>
          <p className={`text-sm md:text-base line-clamp-3 mb-6 ${descCls}`}>{episode.description}</p>

          <div className="mb-6">
            <div className={`h-1.5 rounded-full overflow-hidden ${barBg}`}>
              <div className={`h-full ${barFill} transition-[width] duration-300`} style={{ width: `${progress}%` }} />
            </div>
            <div className={`flex justify-between text-xs mt-2 ${descCls}`}>
              <span>{episode.duration || "—"}</span>
              <span>{episode.published ? new Date(episode.published).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : ""}</span>
            </div>
          </div>

          {episode.audio_url && <audio ref={audioRef} src={episode.audio_url} preload="none" />}

          <div className="flex flex-wrap items-center gap-2 mt-auto">
            <a href={PLATFORM_LINKS.spotify} target="_blank" rel="noreferrer" data-testid="listen-spotify"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1DB954] text-white text-sm hover:opacity-90 transition">
              <Music2 className="w-4 h-4" /> Spotify
            </a>
            <a href={PLATFORM_LINKS.youtube} target="_blank" rel="noreferrer" data-testid="listen-youtube"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF0000] text-white text-sm hover:opacity-90 transition">
              <Youtube className="w-4 h-4" /> YouTube
            </a>
            <a href={PLATFORM_LINKS.apple} target="_blank" rel="noreferrer" data-testid="listen-apple"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition ${smallBtn}`}>
              <ExternalLink className="w-4 h-4" /> Apple
            </a>
            <a href={PLATFORM_LINKS.deezer} target="_blank" rel="noreferrer" data-testid="listen-deezer"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition ${smallBtn}`}>
              <ExternalLink className="w-4 h-4" /> Deezer
            </a>
            <ShareEpisode episode={episode} variant="pill" />
          </div>
        </div>
      </div>
    </div>
  );
}
